import React, { useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material'
import {
  CheckCircleOutline,
  PhotoLibrary,
  Search,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material'
import { Timestamp } from 'firebase/firestore'
import type {
  InstallationNeedKey,
  InstallationNeedStatus,
  InstallationNeedsAssignedCompany,
  InstallationStatus,
} from '@otw/models/logistics'
import {
  INSTALLATION_NEED_KEYS,
  installationNeedMissingRequiredPhoto,
  isInstallationNeedsCompleted,
  listInstallationNeedsMissingPhotos,
} from '@otw/models/logistics'
import type { TransportTruckWithVehicle } from '@/hooks/logistics/useLogistics'
import { useLogistics } from '@/hooks/logistics/useLogistics'
import useAdminTransporter from '@/hooks/admin/useAdminTransporter'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import MediaGalleryDialog, {
  type MediaGalleryItem,
} from '../dialogs/MediaGalleryDialog'

const NEED_LABELS: Record<InstallationNeedKey, string> = {
  anti_theft: 'Anti-theft',
  cameras: 'Cameras',
  backup_sensors: 'Backup sensors',
}

const STATUS_COLOR: Record<InstallationNeedStatus, 'error' | 'warning' | 'success'> = {
  needed: 'error',
  accepted: 'warning',
  installed: 'success',
}

type StatusFilter = 'all' | 'open' | 'completed' | 'hidden' | 'photos_needed' | 'force_closed'
type NeedTypeFilter = 'all' | InstallationNeedKey

const STATUS_FILTERS: Array<{ value: StatusFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'open', label: 'Open' },
  { value: 'completed', label: 'Completed' },
  { value: 'hidden', label: 'Hidden' },
  { value: 'photos_needed', label: 'Photos needed' },
  { value: 'force_closed', label: 'Force-closed' },
]

const NEED_TYPE_FILTERS: Array<{ value: NeedTypeFilter; label: string }> = [
  { value: 'all', label: 'All types' },
  { value: 'anti_theft', label: 'Anti-theft' },
  { value: 'cameras', label: 'Cameras' },
  { value: 'backup_sensors', label: 'Backup sensors' },
]

const FILTER_BUTTON_SX = {
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.9375rem',
  px: 2,
  py: 1,
  minHeight: 40,
  borderRadius: 1,
} as const

type PendingAction =
  | { type: 'hide' | 'complete' | 'unhide'; truckId: string; label: string }
  | null


const truckLabel = (item: TransportTruckWithVehicle): string => {
  const { truck, vehicle } = item
  const ymm = vehicle
    ? `${vehicle.year ?? ''} ${vehicle.make ?? ''} ${vehicle.model ?? ''}`.trim()
    : ''
  const last4 = truck.vin?.slice(-4) ?? ''
  return ymm ? (last4 ? `${ymm} (${last4})` : ymm) : last4 || truck.id
}

const formatAssignedAt = (value: unknown): string | null => {
  if (!value) return null
  let date: Date | null = null
  if (
    typeof value === 'object' &&
    value !== null &&
    'toDate' in value &&
    typeof (value as { toDate: () => Date }).toDate === 'function'
  ) {
    date = (value as { toDate: () => Date }).toDate()
  } else if (typeof value === 'object' && value !== null && 'seconds' in value) {
    date = new Date((value as { seconds: number }).seconds * 1000)
  } else if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value)
    if (!Number.isNaN(parsed.getTime())) date = parsed
  }
  if (!date || Number.isNaN(date.getTime())) return null
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

interface TransportInstallationNeedsTabProps {
  transportTrucksWithVehicles: TransportTruckWithVehicle[]
}

const TransportInstallationNeedsTab: React.FC<TransportInstallationNeedsTabProps> = ({
  transportTrucksWithVehicles,
}) => {
  const theme = useTheme()
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [needTypeFilter, setNeedTypeFilter] = useState<NeedTypeFilter>('all')
  const [pendingAction, setPendingAction] = useState<PendingAction>(null)
  const [busyTruckId, setBusyTruckId] = useState<string | null>(null)
  const [galleryPhotos, setGalleryPhotos] = useState<MediaGalleryItem[] | null>(null)
  const { transporterGroups = [] } = useAdminTransporter()
  const { updateTransportTruckNeeds } = useLogistics()

  const getCompanyName = (groupId: string | undefined) =>
    groupId ? (transporterGroups.find((g) => g.id === groupId)?.name ?? groupId) : null

  const trucksWithNeeds = useMemo(
    () =>
      transportTrucksWithVehicles.filter(
        (t) => t.truck.installation_status && Object.keys(t.truck.installation_status).length > 0
      ),
    [transportTrucksWithVehicles]
  )

  const filteredTrucks = useMemo(() => {
    const query = searchTerm.trim().toLowerCase()
    return trucksWithNeeds.filter((item) => {
      const { truck, vehicle } = item
      const status = truck.installation_status as InstallationStatus
      const completed = isInstallationNeedsCompleted(status)
      const hidden = truck.installation_needs_hidden === true
      const missingPhotos = listInstallationNeedsMissingPhotos(
        status,
        truck.installation_needs_photos
      )
      const forceClosed = truck.installation_needs_force_completed === true

      if (needTypeFilter !== 'all' && !status[needTypeFilter]) return false

      if (query) {
        const haystack = [
          truckLabel(item),
          truck.vin ?? '',
          vehicle?.year ?? '',
          vehicle?.make ?? '',
          vehicle?.model ?? '',
          ...INSTALLATION_NEED_KEYS.map((k) =>
            status[k] ? `${NEED_LABELS[k]} ${getCompanyName(truck.installation_needs_assigned_company?.[k]) ?? ''}` : ''
          ),
        ]
          .join(' ')
          .toLowerCase()
        if (!haystack.includes(query)) return false
      }

      switch (statusFilter) {
        case 'open':
          return !hidden && !completed
        case 'completed':
          return !hidden && completed
        case 'hidden':
          return hidden
        case 'photos_needed':
          return missingPhotos.length > 0
        case 'force_closed':
          return forceClosed
        case 'all':
        default:
          return true
      }
    })
  }, [trucksWithNeeds, searchTerm, statusFilter, needTypeFilter, transporterGroups])

  const hasActiveFilters =
    searchTerm.trim().length > 0 || statusFilter !== 'all' || needTypeFilter !== 'all'

  const clearFilters = () => {
    setSearchTerm('')
    setStatusFilter('all')
    setNeedTypeFilter('all')
  }

  const openPhotosGallery = (
    truckLabelText: string,
    need: InstallationNeedKey,
    photos: NonNullable<TransportTruckWithVehicle['truck']['installation_needs_photos']>[InstallationNeedKey]
  ) => {
    const items: MediaGalleryItem[] = (photos ?? []).map((photo, index) => ({
      key: photo.storage_path || `${need}-${index}`,
      storagePath: photo.storage_path,
      prefetchedUrl: photo.url,
      mediaKind: 'photo' as const,
      noteText: NEED_LABELS[need],
      metaText: [
        photo.file_name,
        formatAssignedAt(photo.uploaded_at)
          ? `Uploaded ${formatAssignedAt(photo.uploaded_at)}`
          : null,
        truckLabelText,
      ]
        .filter(Boolean)
        .join(' · '),
    }))
    setGalleryPhotos(items)
  }

  const handleConfirmAction = async () => {
    if (!pendingAction) return
    const item = trucksWithNeeds.find((t) => t.truck.id === pendingAction.truckId)
    if (!item) return
    const { truck } = item
    const currentStatus = (truck.installation_status ?? {}) as InstallationStatus
    setBusyTruckId(pendingAction.truckId)
    try {
      if (pendingAction.type === 'hide') {
        await updateTransportTruckNeeds({
          truckId: pendingAction.truckId,
          installation_status: currentStatus,
          installation_needs_assigned_company: truck.installation_needs_assigned_company,
          installation_needs_assigned_at: truck.installation_needs_assigned_at,
          installation_needs_hidden: true,
          successMessage: 'Installation needs hidden',
        })
      } else if (pendingAction.type === 'unhide') {
        await updateTransportTruckNeeds({
          truckId: pendingAction.truckId,
          installation_status: currentStatus,
          installation_needs_assigned_company: truck.installation_needs_assigned_company,
          installation_needs_assigned_at: truck.installation_needs_assigned_at,
          installation_needs_hidden: false,
          successMessage: 'Installation needs restored to list',
        })
      } else {
        const completedStatus: InstallationStatus = {}
        for (const key of INSTALLATION_NEED_KEYS) {
          if (currentStatus[key]) completedStatus[key] = 'installed'
        }
        await updateTransportTruckNeeds({
          truckId: pendingAction.truckId,
          installation_status: completedStatus,
          installation_needs_assigned_company: truck.installation_needs_assigned_company,
          installation_needs_assigned_at: truck.installation_needs_assigned_at,
          installation_needs_hidden: false,
          installation_needs_force_completed: true,
          installation_needs_force_completed_at: Timestamp.now(),
          successMessage: 'Installation needs marked completed',
        })
      }
      setPendingAction(null)
    } finally {
      setBusyTruckId(null)
    }
  }

  const confirmCopy =
    pendingAction?.type === 'hide'
      ? {
          title: 'Hide installation needs?',
          message: `Hide "${pendingAction.label}" from the active installation needs list? The tracked needs stay on the truck for accounting/audit.`,
          confirmText: 'Hide',
        }
      : pendingAction?.type === 'unhide'
        ? {
            title: 'Unhide installation needs?',
            message: `Restore "${pendingAction.label}" to the installation needs list?`,
            confirmText: 'Unhide',
          }
        : {
            title: 'Mark installation needs completed?',
            message: `Mark "${pendingAction?.label ?? 'this truck'}" installation needs as completed? This force-closes open needs without deleting them. Required camera/sensor photos can still be uploaded later.`,
            confirmText: 'Mark completed',
          }

  if (trucksWithNeeds.length === 0) {
    return (
      <Box sx={{ px: 2, mt: 2 }}>
        <Alert severity="info">No installation needs are tracked on any transport trucks.</Alert>
      </Box>
    )
  }

  return (
    <Box sx={{ px: 2, mt: 2, pb: 3 }}>
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Installation needs
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Review assigned needs across trucks, hide or mark completed without deleting records, and
            view uploaded working-condition photos.
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by truck, VIN, company, or need type..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search color="action" />
                </InputAdornment>
              ),
            }}
          />
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}
            >
              Status
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={1}>
              {STATUS_FILTERS.map((filter) => {
                const selected = statusFilter === filter.value
                return (
                  <Button
                    key={filter.value}
                    size="small"
                    color={selected ? 'secondary' : 'inherit'}
                    variant={selected ? 'contained' : 'outlined'}
                    onClick={() => setStatusFilter(filter.value)}
                    sx={FILTER_BUTTON_SX}
                  >
                    {filter.label}
                  </Button>
                )
              })}
            </Box>
          </Box>
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}
            >
              Need type
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={1}>
              {NEED_TYPE_FILTERS.map((filter) => {
                const selected = needTypeFilter === filter.value
                return (
                  <Button
                    key={filter.value}
                    size="small"
                    color={selected ? 'primary' : 'inherit'}
                    variant={selected ? 'contained' : 'outlined'}
                    onClick={() => setNeedTypeFilter(filter.value)}
                    sx={FILTER_BUTTON_SX}
                  >
                    {filter.label}
                  </Button>
                )
              })}
            </Box>
          </Box>
          {hasActiveFilters ? (
            <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
              <Typography variant="caption" color="text.secondary">
                Showing {filteredTrucks.length.toLocaleString('en-US')} of{' '}
                {trucksWithNeeds.length.toLocaleString('en-US')}
              </Typography>
              <Button size="small" onClick={clearFilters} sx={{ textTransform: 'none' }}>
                Clear filters
              </Button>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {trucksWithNeeds.length.toLocaleString('en-US')} truck
              {trucksWithNeeds.length === 1 ? '' : 's'} with installation needs
            </Typography>
          )}
        </CardContent>
      </Card>

      {filteredTrucks.length === 0 ? (
        <Alert severity="info">No installation needs match your search or filters.</Alert>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {filteredTrucks.map((item) => {
            const { truck } = item
            const status = truck.installation_status as InstallationStatus
            const assigned = truck.installation_needs_assigned_company as
              | InstallationNeedsAssignedCompany
              | undefined
            const label = truckLabel(item)
            const hidden = truck.installation_needs_hidden === true
            const completed = isInstallationNeedsCompleted(status)
            const isBusy = busyTruckId === truck.id
            const needKeys = INSTALLATION_NEED_KEYS.filter((key) => {
              if (!status[key]) return false
              if (needTypeFilter !== 'all' && key !== needTypeFilter) return false
              return true
            })

            return (
              <Card
                key={truck.id}
                variant="outlined"
                sx={{
                  borderColor: hidden
                    ? alpha(theme.palette.text.secondary, 0.3)
                    : completed
                      ? alpha(theme.palette.success.main, 0.35)
                      : alpha(theme.palette.divider, 0.9),
                  bgcolor: hidden
                    ? alpha(theme.palette.action.hover, 0.04)
                    : completed
                      ? alpha(theme.palette.success.main, 0.03)
                      : 'background.paper',
                }}
              >
                <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                  <Box
                    display="flex"
                    alignItems="flex-start"
                    justifyContent="space-between"
                    gap={1}
                    mb={1}
                  >
                    <Box minWidth={0}>
                      <Typography variant="subtitle2" fontWeight={700} noWrap title={label}>
                        {label}
                      </Typography>
                      {truck.vin ? (
                        <Typography variant="caption" color="text.secondary">
                          VIN {truck.vin}
                        </Typography>
                      ) : null}
                    </Box>
                    <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap" justifyContent="flex-end">
                      {hidden ? <Chip label="Hidden" size="small" variant="outlined" /> : null}
                      {completed ? <Chip label="Completed" size="small" color="success" /> : null}
                      {truck.installation_needs_force_completed ? (
                        <Chip label="Force-closed" size="small" color="warning" variant="outlined" />
                      ) : null}
                      {hidden ? (
                        <Tooltip title="Unhide">
                          <span>
                            <IconButton
                              size="small"
                              disabled={isBusy || Boolean(busyTruckId)}
                              onClick={() =>
                                setPendingAction({
                                  type: 'unhide',
                                  truckId: truck.id,
                                  label: label || 'this truck',
                                })
                              }
                            >
                              {isBusy ? (
                                <CircularProgress size={16} />
                              ) : (
                                <Visibility fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      ) : (
                        <>
                          {!completed ? (
                            <Tooltip title="Mark completed">
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={isBusy || Boolean(busyTruckId)}
                                  onClick={() =>
                                    setPendingAction({
                                      type: 'complete',
                                      truckId: truck.id,
                                      label: label || 'this truck',
                                    })
                                  }
                                >
                                  {isBusy ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <CheckCircleOutline fontSize="small" />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          ) : null}
                          <Tooltip title="Hide from active list">
                            <span>
                              <IconButton
                                size="small"
                                disabled={isBusy || Boolean(busyTruckId)}
                                onClick={() =>
                                  setPendingAction({
                                    type: 'hide',
                                    truckId: truck.id,
                                    label: label || 'this truck',
                                  })
                                }
                              >
                                {isBusy ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <VisibilityOff fontSize="small" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </Box>

                  <Box display="flex" flexDirection="column" gap={1}>
                    {needKeys.map((key) => {
                      const needStatus = status[key]!
                      const companyName = getCompanyName(assigned?.[key])
                      const assignedAt = formatAssignedAt(truck.installation_needs_assigned_at?.[key])
                      const photos = truck.installation_needs_photos?.[key] ?? []
                      const missingPhoto = installationNeedMissingRequiredPhoto(
                        key,
                        status,
                        truck.installation_needs_photos
                      )

                      return (
                        <Box
                          key={`${truck.id}-${key}`}
                          sx={{
                            p: 1.25,
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: missingPhoto
                              ? alpha(theme.palette.warning.main, 0.45)
                              : 'divider',
                            bgcolor: 'background.paper',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            gap: 1,
                          }}
                        >
                          <Box minWidth={0}>
                            <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
                              <Chip
                                label={NEED_LABELS[key]}
                                size="small"
                                color={STATUS_COLOR[needStatus]}
                              />
                              <Chip
                                label={needStatus}
                                size="small"
                                variant="outlined"
                                sx={{ textTransform: 'capitalize' }}
                              />
                              {companyName ? (
                                <Typography variant="caption" color="text.secondary">
                                  {companyName}
                                </Typography>
                              ) : (
                                <Typography variant="caption" color="text.secondary">
                                  Any company with a leg
                                </Typography>
                              )}
                            </Box>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {assignedAt ? `Assigned ${assignedAt}` : 'Assigned date unavailable'}
                            </Typography>
                            {missingPhoto ? (
                              <Typography variant="caption" color="warning.main" display="block">
                                Working-condition photo still required
                              </Typography>
                            ) : photos.length > 0 ? (
                              <Typography variant="caption" color="success.main" display="block">
                                {photos.length.toLocaleString('en-US')} photo
                                {photos.length === 1 ? '' : 's'} uploaded
                              </Typography>
                            ) : null}
                          </Box>
                          {photos.length > 0 ? (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<PhotoLibrary />}
                              sx={{ textTransform: 'none' }}
                              onClick={() => openPhotosGallery(label, key, photos)}
                            >
                              View photos ({photos.length.toLocaleString('en-US')})
                            </Button>
                          ) : null}
                        </Box>
                      )
                    })}
                  </Box>
                </CardContent>
              </Card>
            )
          })}
        </Box>
      )}

      <ConfirmDialog
        open={pendingAction != null}
        title={confirmCopy.title}
        message={confirmCopy.message}
        confirmText={confirmCopy.confirmText}
        cancelText="Cancel"
        severity="warning"
        onCancel={() => {
          if (busyTruckId) return
          setPendingAction(null)
        }}
        onConfirm={() => {
          void handleConfirmAction()
        }}
      />

      <MediaGalleryDialog
        open={Boolean(galleryPhotos?.length)}
        onClose={() => setGalleryPhotos(null)}
        photos={galleryPhotos ?? []}
        videos={[]}
      />
    </Box>
  )
}

export default TransportInstallationNeedsTab
